import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRangePicker } from '@/components/ui/date-picker';
import { TimeGranularitySelect, type TimeGranularity } from '@/components/ui/time-granularity-select';
import { SimpleBarChart, DataTable } from '@/components/ui/simple-chart';
import { client, type OrdersReport } from '@vn-propane/shared';
import { Badge } from '@/components/ui/badge';

function getDefaultDateRange(): { start: Date; end: Date } {
  const end = new Date();
  end.setHours(23, 59, 59, 999);
  const start = new Date();
  start.setDate(start.getDate() - 29);
  start.setHours(0, 0, 0, 0);
  return { start, end };
}

// Normalize status from camelCase to snake_case (e.g., "inProgress" -> "in_progress")
function normalizeStatus(status: string): string {
  return status.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '');
}

export default function OrdersReportPage() {
  const { id: orgId } = useParams<{ id: string }>();
  const { t } = useTranslation(['common', 'orders']);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<OrdersReport | null>(null);

  const defaultRange = getDefaultDateRange();
  const [startDate, setStartDate] = useState<Date | undefined>(defaultRange.start);
  const [endDate, setEndDate] = useState<Date | undefined>(defaultRange.end);
  const [granularity, setGranularity] = useState<TimeGranularity>('day');

  const fetchData = useCallback(async () => {
    if (!orgId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const response = await client.get({
        url: `/gas_admin/organizations/${orgId}/reports/orders`,
        query: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString(),
          granularity,
        },
      });

      if (response.error) {
        setError('Failed to load orders report');
        return;
      }

      setData((response.data as { data: OrdersReport } | undefined)?.data ?? null);
    } catch (err) {
      setError('Failed to load orders report');
    } finally {
      setLoading(false);
    }
  }, [orgId, startDate, endDate, granularity]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const statusColors: Record<string, string> = {
    pending: 'bg-carbon-200 text-carbon-800',
    assigned: 'bg-info/10 text-info',
    in_progress: 'bg-warning text-carbon-950',
    arrived: 'bg-propane-100 text-propane-700',
    delivered: 'bg-success/10 text-success',
    cancelled: 'bg-destructive/10 text-destructive',
  };

  // Fallback labels in case translations aren't loaded
  const fallbackLabels: Record<string, string> = {
    pending: 'Pending',
    assigned: 'Assigned',
    in_progress: 'In Progress',
    arrived: 'Arrived',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
  };

  const getStatusLabel = (status: string): string => {
    const normalized = normalizeStatus(status);
    const translated = t(`orders:status.${normalized}`, { defaultValue: '' });
    // If translation returns empty or the key itself, use fallback
    if (translated && !translated.startsWith('orders.') && !translated.startsWith('orders:')) {
      return translated;
    }
    return fallbackLabels[normalized] || status;
  };

  const getStatusColor = (status: string): string => {
    const normalized = normalizeStatus(status);
    return statusColors[normalized] || 'bg-carbon-200 text-carbon-800';
  };

  return (
    <div className="space-y-6">
      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>{t('reports.filters')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <DateRangePicker
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
            startLabel={t('labels.from')}
            endLabel={t('labels.to')}
          />
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium">{t('reports.granularityLabel')}:</span>
            <TimeGranularitySelect
              value={granularity}
              onChange={setGranularity}
              className="w-40"
            />
          </div>
        </CardContent>
      </Card>

      {loading && (
        <div className="text-center py-8 text-muted-foreground">
          {t('labels.loading')}
        </div>
      )}

      {error && (
        <div className="text-center py-8 text-destructive">
          {error}
        </div>
      )}

      {!loading && !error && data && (
        <>
          {/* Summary */}
          <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('reports.metrics.totalOrders')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tabular-nums tracking-tight">{data.time_series.summary.count}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('reports.metrics.average')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tabular-nums tracking-tight">
                  {Number(data.time_series.summary.average).toFixed(1)}/{t(`reports.granularity.${granularity}`)}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('reports.metrics.min')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tabular-nums tracking-tight">{data.time_series.summary.min}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                  {t('reports.metrics.max')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-extrabold tabular-nums tracking-tight">{data.time_series.summary.max}</div>
              </CardContent>
            </Card>
          </div>

          {/* Chart */}
          <Card>
            <CardHeader>
              <CardTitle>{t('reports.ordersOverTime')}</CardTitle>
            </CardHeader>
            <CardContent>
              <SimpleBarChart
                data={data.time_series.data}
                height={250}
                formatValue={(v) => v.toString()}
              />
            </CardContent>
          </Card>

          {/* Status Breakdown and Data Table */}
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('reports.ordersByStatus')}</CardTitle>
              </CardHeader>
              <CardContent>
                {data.status_breakdown.length === 0 ? (
                  <p className="text-muted-foreground">{t('reports.noData')}</p>
                ) : (
                  <div className="space-y-3">
                    {data.status_breakdown.map((item) => (
                      <div key={item.status} className="flex justify-between items-center">
                        <Badge className={getStatusColor(item.status)}>
                          {getStatusLabel(item.status)}
                        </Badge>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>{t('reports.dataTable')}</CardTitle>
              </CardHeader>
              <CardContent>
                <DataTable
                  data={data.time_series.data}
                  valueLabel={t('reports.metrics.orders')}
                  dateLabel={t('reports.tableDate')}
                  countLabel={t('reports.tableCount')}
                  formatValue={(v) => v.toString()}
                />
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
